import P from 'parsimmon';
import { z } from 'zod';

import {
  assertNever,
  EXPRESSION_TYPES,
  ExpressionType,
  StepDefinitionId,
  Temporal,
  TypeFrom,
  TypeMap,
} from '@willow/types-iso';

import { FunctionContext, PerCallFunctionContext, SharedFunctionContext } from './FunctionContext';
import {
  AnyFunctionDefinition,
  FunctionLibrary,
  getFunctionSignatureWithoutReturnType,
  spreadType,
} from './FunctionDefinition';
import { ALL_PARSEABLE_FUNCTIONS } from './functionRegistries';

/**
 * Core expression engine shared by every use-case. The two things that vary between use-cases —
 * the runtime context (`ctx`) and the set of runnable functions (`functions`) — are left to the
 * subclass; everything else (parsing, type resolution, evaluation, time analysis) lives here.
 *
 * Parsing/validation is intentionally registry-agnostic: it resolves against the full language
 * (`ALL_PARSEABLE_FUNCTIONS`, the workflow superset) so any expression type-checks and unknown
 * functions are rejected. Whether a parsed function can actually *run* is decided at eval time
 * against the subclass's own `functions`, so an email expression that references a workflow-only
 * function parses fine but throws when evaluated (callers fall back to the default template).
 */
export abstract class AbstractExpressionEvaluator<TFunctionCtx extends SharedFunctionContext> {
  // The evaluator-construction context — everything the implementations need except the per-call `now`.
  constructor(protected readonly ctx: Omit<TFunctionCtx, keyof PerCallFunctionContext>) {}

  // The functions this evaluator can run. Subclasses pin the context tier (shared vs. full workflow).
  protected abstract readonly functions: FunctionLibrary<TFunctionCtx>;

  private cachedFunctionsSansReturnType?: Map<string, AnyFunctionDefinition<TFunctionCtx>>;

  private get functionsSansReturnType(): Map<string, AnyFunctionDefinition<TFunctionCtx>> {
    if (!this.cachedFunctionsSansReturnType) {
      this.cachedFunctionsSansReturnType = new Map(
        Object.values(this.functions).map((functionDefinition) => [
          getFunctionSignatureWithoutReturnType({
            name: functionDefinition.name,
            parameterTypes:
              'spread' in functionDefinition.parameters
                ? { spread: functionDefinition.parameters.spread.type }
                : functionDefinition.parameters.map((p) => p.type),
          }),
          functionDefinition,
        ]),
      );
    }
    return this.cachedFunctionsSansReturnType;
  }

  public evaluate<T extends ExpressionType>(
    expression: Expression<T>,
    perCallCtx: PerCallFunctionContext,
  ): TypeFrom<(typeof EXPRESSION_TYPES)[T]> {
    const parsedExpression = AbstractExpressionEvaluator.parse(expression);
    return this.evaluateNode(parsedExpression, { ...this.ctx, now: perCallCtx.now } as TFunctionCtx) as TypeFrom<
      (typeof EXPRESSION_TYPES)[T]
    >;
  }

  private evaluateNode(expression: ExpressionNode, ctx: TFunctionCtx): unknown {
    switch (expression.name) {
      case 'BOOLEAN_LITERAL':
      case 'NUMBER_LITERAL':
      case 'STRING_LITERAL':
        return expression.value;
      case 'FUNCTION_APPLICATION': {
        const functionDefinition = this.lookupRunnableFunction(expression);
        return (functionDefinition.implementation as any)(
          ctx,
          ...expression.value.parameters.map((p, i) => {
            if (!('spread' in functionDefinition.parameters) && functionDefinition.parameters[i].isLazy) {
              return () => this.evaluateNode(p, ctx);
            }
            return this.evaluateNode(p, ctx);
          }),
        );
      }
      default:
        return assertNever(expression);
    }
  }

  // Resolve a parsed function application to a definition this evaluator can actually run. Throws if
  // the function exists in the language but not in this evaluator's registry (e.g. a workflow-only
  // function referenced from an email expression).
  private lookupRunnableFunction(
    functionApplicationNode: Pick<FunctionApplicationNode, 'value' | 'start'>,
  ): AnyFunctionDefinition<TFunctionCtx> {
    const signatures = [
      ...AbstractExpressionEvaluator.getAllPossibleSignaturesWithoutReturnType(functionApplicationNode.value),
    ];
    for (const signature of signatures) {
      const functionDefinition = this.functionsSansReturnType.get(signature);
      if (functionDefinition != null) {
        return functionDefinition;
      }
    }

    throw new Error(
      `Function with signature ${signatures[0]} is not available in this evaluator at line ${functionApplicationNode.start.line} column ${functionApplicationNode.start.column}`,
    );
  }

  public getNextTimeExpressionWillChange(
    expression: string,
    perCallCtx: PerCallFunctionContext,
    options: { changeTo?: boolean } = {},
  ): Temporal.ZonedDateTime | undefined {
    const ctx = { ...this.ctx, now: perCallCtx.now } as TFunctionCtx;
    const parsedExpression = AbstractExpressionEvaluator.parse(expression);

    const currentValue = this.evaluateNode(parsedExpression, ctx);

    if (options.changeTo !== undefined && currentValue === options.changeTo) {
      return undefined;
    }

    if (
      AbstractExpressionEvaluator.expressionIsAlwaysTrue(expression) ||
      AbstractExpressionEvaluator.expressionIsAlwaysFalse(expression)
    ) {
      return undefined;
    }

    const timestamps = this.getTimestampsFromExpression(parsedExpression, ctx)
      .filter((t) => Temporal.ZonedDateTime.compare(ctx.now(), t) <= 0)
      .sort((a, b) => Temporal.ZonedDateTime.compare(a, b));

    for (const timestamp of timestamps) {
      const valueAtTimestamp = this.evaluateNode(parsedExpression, { ...ctx, now: () => timestamp });

      if (options.changeTo !== undefined && valueAtTimestamp === options.changeTo) {
        return timestamp;
      }
      if (valueAtTimestamp !== currentValue) {
        return timestamp;
      }
    }

    return undefined;
  }

  private getTimestampsFromExpression(expression: ExpressionNode, ctx: TFunctionCtx): Temporal.ZonedDateTime[] {
    switch (expression.name) {
      case 'BOOLEAN_LITERAL':
      case 'NUMBER_LITERAL':
      case 'STRING_LITERAL':
        return [];
      case 'FUNCTION_APPLICATION': {
        const timestamps: Temporal.ZonedDateTime[] = [];

        if (expression.type === 'zonedDateTime' || expression.type === 'plainDate') {
          try {
            const result = this.evaluateNode(expression, ctx);
            if (result instanceof Temporal.ZonedDateTime) {
              timestamps.push(result);
            }
            if (result instanceof Temporal.PlainDate) {
              timestamps.push(result.toZonedDateTime(ctx.timeZone));
            }
          } catch (_error: unknown) {
            // Skip this timestamp if evaluation fails
          }
        }

        const parameterTimestamps = expression.value.parameters.flatMap((p) =>
          this.getTimestampsFromExpression(p, ctx),
        );
        timestamps.push(...parameterTimestamps);

        return timestamps;
      }
      default:
        return assertNever(expression);
    }
  }

  // ---------------------------------------------------------------------------
  // Language-level (registry-agnostic) statics: parsing, type resolution, and
  // the authoring helpers. These resolve against the full language superset.
  // ---------------------------------------------------------------------------

  private static readonly PARSE_FUNCTIONS_SANS_RETURN_TYPE: Record<string, AnyFunctionDefinition<FunctionContext>> =
    Object.fromEntries(
      Object.values(ALL_PARSEABLE_FUNCTIONS).map(
        (functionDefinition): [string, AnyFunctionDefinition<FunctionContext>] => [
          getFunctionSignatureWithoutReturnType({
            name: functionDefinition.name,
            parameterTypes:
              'spread' in functionDefinition.parameters
                ? { spread: functionDefinition.parameters.spread.type }
                : functionDefinition.parameters.map((p) => p.type),
          }),
          functionDefinition,
        ],
      ),
    );

  public static parse(expression: string): ExpressionNode {
    const result = AbstractExpressionEvaluator.EXPRESSION_LANGUAGE.expression
      .trim(P.whitespace.many())
      .skip(P.eof)
      .parse(expression);
    if (result.status === false) {
      throw new Error(
        `Failed to parse expression: ${result.expected} at line ${result.index.line} column ${result.index.column}`,
      );
    }
    return result.value;
  }

  private static readonly EXPRESSION_LANGUAGE = P.createLanguage<ExpressionLanguageSpec>({
    booleanLiteral: () =>
      P.regexp(/true|false/)
        .map((n) => n === 'true')
        .desc('BOOLEAN_LITERAL')
        .node('BOOLEAN_LITERAL')
        .map((n) => ({ ...n, type: EXPRESSION_TYPES.boolean.name })),
    numberLiteral: () =>
      P.regexp(/-?(0|[1-9][0-9]*)([.][0-9]+)?([eE][+-]?[0-9]+)?/)
        .map((n) => +n)
        .desc('NUMBER_LITERAL')
        .node('NUMBER_LITERAL')
        .map((n) => ({ ...n, type: EXPRESSION_TYPES.number.name })),
    stringLiteral: () =>
      P.regexp(/"((?:\\.|.)*?)"/, 1)
        .map(interpretEscapes)
        .desc('STRING_LITERAL')
        .node('STRING_LITERAL')
        .map((n) => ({ ...n, type: EXPRESSION_TYPES.string.name })),
    functionApplication: ({ expression }) =>
      P.seq(
        P.regexp(/[a-zA-Z_][a-zA-Z0-9_]*/).desc('IDENTIFIER'),
        P.string('('),
        P.sepBy(expression, ARGUMENT_SEPARATOR).skip(ARGUMENT_SEPARATOR.atMost(1)).trim(P.whitespace.many()),
        P.string(')'),
      )
        .map(([name, , parameters]) => ({ name, parameters }))
        .desc('FUNCTION_APPLICATION')
        .node('FUNCTION_APPLICATION')
        .map((n) => {
          const functionDefinition = AbstractExpressionEvaluator.lookupFunctionDefinitionForParse(n);
          return { ...n, type: functionDefinition.returnType.name as ExpressionType };
        }),
    expression: ({ functionApplication, numberLiteral, stringLiteral, booleanLiteral }) =>
      P.alt(numberLiteral, stringLiteral, booleanLiteral, functionApplication),
  });

  // Resolve a function purely to type its parse node. Uses the full language superset, so this is
  // about "does this function exist and what does it return", not "can this evaluator run it".
  private static lookupFunctionDefinitionForParse(
    functionApplicationNode: Pick<FunctionApplicationNode, 'value' | 'start'>,
  ): AnyFunctionDefinition<FunctionContext> {
    const signatures = [
      ...AbstractExpressionEvaluator.getAllPossibleSignaturesWithoutReturnType(functionApplicationNode.value),
    ];
    for (const signature of signatures) {
      const functionDefinition = AbstractExpressionEvaluator.PARSE_FUNCTIONS_SANS_RETURN_TYPE[signature];
      if (functionDefinition != null) {
        return functionDefinition;
      }
    }

    throw new Error(
      `Function with signature ${signatures[0]} not defined at line ${functionApplicationNode.start.line} column ${functionApplicationNode.start.column}`,
    );
  }

  private static *getAllPossibleSignaturesWithoutReturnType({
    parameters,
    name,
  }: FunctionApplicationNode['value']): Generator<string> {
    yield getFunctionSignatureWithoutReturnType({
      name,
      parameterTypes: parameters.map((p) => EXPRESSION_TYPES[p.type]),
    });

    const [first, ...rest] = parameters;

    if (parameters.length >= 1 && rest.every((p) => p.type === first.type)) {
      yield getFunctionSignatureWithoutReturnType({
        name,
        parameterTypes: spreadType(EXPRESSION_TYPES[first.type]),
      });
    }
  }

  public static expressionIsAlwaysTrue(expression: string): boolean {
    const parsedExpression = AbstractExpressionEvaluator.parse(expression);
    return AbstractExpressionEvaluator.expressionNodeIsAlwaysTrue(parsedExpression);
  }

  private static expressionNodeIsAlwaysTrue(expression: ExpressionNode): boolean {
    switch (expression.name) {
      case 'BOOLEAN_LITERAL':
        return expression.value === true;
      case 'NUMBER_LITERAL':
      case 'STRING_LITERAL':
        return false;
      case 'FUNCTION_APPLICATION': {
        const { name, parameters } = expression.value;

        if (name === 'not') {
          return AbstractExpressionEvaluator.expressionNodeIsAlwaysFalse(parameters[0]);
        }

        if (name === 'or') {
          return parameters.some((p) => AbstractExpressionEvaluator.expressionNodeIsAlwaysTrue(p));
        }

        if (name === 'and') {
          return parameters.every((p) => AbstractExpressionEvaluator.expressionNodeIsAlwaysTrue(p));
        }

        return false;
      }
      default:
        return assertNever(expression);
    }
  }

  public static expressionIsAlwaysFalse(expression: string): boolean {
    const parsedExpression = AbstractExpressionEvaluator.parse(expression);
    return AbstractExpressionEvaluator.expressionNodeIsAlwaysFalse(parsedExpression);
  }

  public static findExpressionNodes(
    expression: string,
    predicate: (node: ExpressionNode) => boolean,
  ): ExpressionNode[] {
    const parsedExpression = AbstractExpressionEvaluator.parse(expression);
    return AbstractExpressionEvaluator.findExpressionNodesInTree(parsedExpression, predicate);
  }

  private static findExpressionNodesInTree(
    expression: ExpressionNode,
    predicate: (node: ExpressionNode) => boolean,
  ): ExpressionNode[] {
    const results: ExpressionNode[] = [];
    if (predicate(expression)) {
      results.push(expression);
    }

    switch (expression.name) {
      case 'FUNCTION_APPLICATION': {
        const { parameters } = expression.value;
        for (const parameter of parameters) {
          results.push(...AbstractExpressionEvaluator.findExpressionNodesInTree(parameter, predicate));
        }
        break;
      }
      case 'BOOLEAN_LITERAL':
      case 'NUMBER_LITERAL':
      case 'STRING_LITERAL':
        break;
      default:
        assertNever(expression);
    }

    return results;
  }

  private static expressionNodeIsAlwaysFalse(expression: ExpressionNode): boolean {
    switch (expression.name) {
      case 'BOOLEAN_LITERAL':
        return expression.value === false;
      case 'NUMBER_LITERAL':
      case 'STRING_LITERAL':
        return false;
      case 'FUNCTION_APPLICATION': {
        const { name, parameters } = expression.value;

        if (name === 'not') {
          return AbstractExpressionEvaluator.expressionNodeIsAlwaysTrue(parameters[0]);
        }

        if (name === 'and') {
          return parameters.some((p) => AbstractExpressionEvaluator.expressionNodeIsAlwaysFalse(p));
        }

        if (name === 'or') {
          return parameters.every((p) => AbstractExpressionEvaluator.expressionNodeIsAlwaysFalse(p));
        }

        return false;
      }
      default:
        return assertNever(expression);
    }
  }

  public static assertType<T extends ExpressionType>(expression: string, type: T): Expression<T> {
    const parsed = AbstractExpressionEvaluator.parse(expression);
    if (parsed.type !== type) {
      throw new Error(`Expression is not of type ${type}`);
    }
    return expression as Expression<T>;
  }

  public static assertBoolean = (expression: string): Expression<'boolean'> =>
    AbstractExpressionEvaluator.assertType(expression, 'boolean');

  private static template = (strings: TemplateStringsArray, ...values: InterpolatableValue[]): Expression =>
    strings.reduce((acc, str, i) => {
      const value = values[i];
      if (isNestedInterpolatableValue(value)) {
        return acc + str + value.expression;
      }

      if (value == null) {
        return acc + str;
      }

      if (value instanceof Temporal.ZonedDateTime) {
        return `${acc + str}zonedDateTime(${JSON.stringify(value.toString())})`;
      }

      if (value instanceof Temporal.Duration) {
        return `${acc + str}duration(${JSON.stringify(value.toString())})`;
      }

      if (value instanceof Temporal.PlainDate) {
        return `${acc + str}plainDate(${JSON.stringify(value.toString())})`;
      }

      if (value instanceof Temporal.PlainTime) {
        return `${acc + str}plainTime(${JSON.stringify(value.toString())})`;
      }

      if (value instanceof Date) {
        return `${acc + str}date(${JSON.stringify(value.toISOString())})`;
      }

      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return acc + str + JSON.stringify(value);
      }

      return assertNever(value);
    }, '') as Expression;

  public static nest = (expression: Expression): NestedInterpolatableValue => ({ __type: 'nested', expression });

  public static boolean = (strings: TemplateStringsArray, ...values: InterpolatableValue[]): Expression<'boolean'> =>
    AbstractExpressionEvaluator.assertType(AbstractExpressionEvaluator.template(strings, ...values), 'boolean');

  public static plainDate = (
    strings: TemplateStringsArray,
    ...values: InterpolatableValue[]
  ): Expression<'plainDate'> =>
    AbstractExpressionEvaluator.assertType(AbstractExpressionEvaluator.template(strings, ...values), 'plainDate');

  public static zonedDateTime = (
    strings: TemplateStringsArray,
    ...values: InterpolatableValue[]
  ): Expression<'zonedDateTime'> =>
    AbstractExpressionEvaluator.assertType(AbstractExpressionEvaluator.template(strings, ...values), 'zonedDateTime');

  public static string = (strings: TemplateStringsArray, ...values: InterpolatableValue[]): Expression<'string'> =>
    AbstractExpressionEvaluator.assertType(AbstractExpressionEvaluator.template(strings, ...values), 'string');

  public static number = (strings: TemplateStringsArray, ...values: InterpolatableValue[]): Expression<'number'> =>
    AbstractExpressionEvaluator.assertType(AbstractExpressionEvaluator.template(strings, ...values), 'number');

  public static duration = (strings: TemplateStringsArray, ...values: InterpolatableValue[]): Expression<'duration'> =>
    AbstractExpressionEvaluator.assertType(AbstractExpressionEvaluator.template(strings, ...values), 'duration');

  public static getReferencedStepDefinitionIds(_selfId: StepDefinitionId, _expression: Expression): StepDefinitionId[] {
    // TODO implement
    return [];
  }

  public static expressionCanBeTrueWithoutStepDependency(_expression: Expression): boolean {
    // TODO implement
    return true;
  }
}

type InterpolatableValue = TypeMap[keyof TypeMap] | NestedInterpolatableValue;

type NestedInterpolatableValue = { __type: 'nested'; expression: Expression };

const isNestedInterpolatableValue = (value: unknown): value is NestedInterpolatableValue =>
  typeof value === 'object' && value != null && '__type' in value && value.__type === 'nested';

const ARGUMENT_SEPARATOR = P.string(',').trim(P.whitespace.many());

interface ExpressionLanguageSpec {
  booleanLiteral: BooleanLiteralNode;
  numberLiteral: NumberLiteralNode;
  stringLiteral: StringLiteralNode;
  functionApplication: FunctionApplicationNode;
  expression: ExpressionNode;
}

type BooleanLiteralNode = P.Node<'BOOLEAN_LITERAL', boolean> & { type: (typeof EXPRESSION_TYPES.boolean)['name'] };
type NumberLiteralNode = P.Node<'NUMBER_LITERAL', number> & { type: (typeof EXPRESSION_TYPES.number)['name'] };
type StringLiteralNode = P.Node<'STRING_LITERAL', string> & { type: (typeof EXPRESSION_TYPES.string)['name'] };
type FunctionApplicationNode<T extends ExpressionType = ExpressionType> = P.Node<
  'FUNCTION_APPLICATION',
  { name: string; parameters: ExpressionNode<T>[] }
> & {
  type: T;
};
export type ExpressionNode<T extends ExpressionType = ExpressionType> =
  | BooleanLiteralNode
  | NumberLiteralNode
  | StringLiteralNode
  | FunctionApplicationNode<T>;

export type Expression<T extends ExpressionType = ExpressionType> = string & { __brand: 'Expression'; __type: T };
export const Expression = <T extends ExpressionType>(
  type: T,
): z.ZodPipe<z.ZodString, z.ZodTransform<Awaited<Expression<T>>, string>> =>
  z.string().transform((val, ctx): Expression<T> => {
    try {
      const parsed = AbstractExpressionEvaluator.parse(val);
      if (parsed.type !== type) {
        ctx.addIssue({
          code: 'custom',
          message: `Expression is not of type ${type} (got ${parsed.type}): ${val}`,
        });
        return z.NEVER;
      }
      return val as Expression<T>;
    } catch (e) {
      ctx.addIssue({ code: 'custom', message: `Expression is not valid: ${e}` });
      return z.NEVER;
    }
  });

// Turn escaped characters into real ones (e.g. "\\n" becomes "\n").
const ESCAPES = Object.create(null);
ESCAPES.b = '\b';
ESCAPES.f = '\f';
ESCAPES.n = '\n';
ESCAPES.r = '\r';
ESCAPES.t = '\t';

function interpretEscapes(str: string) {
  return str.replace(/\\(u[0-9a-fA-F]{4}|[^u])/, (_, escape) => {
    const type = escape.charAt(0);
    const hex = escape.slice(1);
    if (type === 'u') {
      return String.fromCharCode(parseInt(hex, 16));
    }
    if (type in ESCAPES) {
      return ESCAPES[type as keyof typeof ESCAPES];
    }
    return type;
  });
}
